import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUp, ArrowDown, Save, Loader2, ShieldCheck, LogOut, Upload, Star, Trash2, Image as ImageIcon, ImagePlus, StarOff, RefreshCw } from 'lucide-react';
import { adminApi, logoutAdmin } from './adminApi';
import { toAssetUrl } from '@/data/runtimeApi';
import { uploadFiles } from './uploadInBatches';

const toUrl = toAssetUrl;

const LOGO_SIZES = [
  { value: 'default', label: 'Default (28-36 px)' },
  { value: 'md', label: 'Medium (-15%)' },
  { value: 'sm', label: 'Small (-30%)' },
];

const ASPECT_RATIOS = [
  { value: '', label: 'Automático' },
  { value: '3/4', label: '3 / 4  (retrato)' },
  { value: '4/5', label: '4 / 5  (retrato ligero)' },
  { value: '5/4', label: '5 / 4  (paisaje ligero)' },
  { value: '3 / 5.2', label: '3 / 5.2 (retrato alto)' },
];

export default function AdminProyectoEdit() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [galleryProgress, setGalleryProgress] = useState(null);
  const mainRef = useRef();
  const clientLogoRef = useRef();
  const featuredLogoRef = useRef();
  const galleryRef = useRef();

  const load = async () => {
    const [projRes, svcRes] = await Promise.all([
      adminApi.get(`/proyectos/${id}`),
      adminApi.get('/servicios'),
    ]);
    setItem(projRes.data);
    setServicios(svcRes.data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = (obj) => setItem((cur) => ({ ...cur, ...obj }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        slug: item.slug,
        title: item.title,
        client: item.client,
        subtitle: item.subtitle,
        category: item.category,
        description: item.description,
        featuredOnHome: item.featuredOnHome,
        featuredLogoSize: item.featuredLogoSize,
        featuredAspect: item.featuredAspect,
      };
      const { data } = await adminApi.patch(`/proyectos/${id}`, body);
      setItem(data);
      alert('Cambios guardados.');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const uploadSingle = async (field, ref) => {
    const f = ref.current?.files?.[0];
    if (!f) return;
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await adminApi.post(`/proyectos/${id}/image?field=${field}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      patch({ [field]: data.url });
      if (ref.current) ref.current.value = '';
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al subir');
    } finally { setUploadingField(null); }
  };

  const runUploadGallery = async (filesToUpload) => {
    if (!filesToUpload || filesToUpload.length === 0) return;
    setUploadingGallery(true);
    setGalleryProgress({ completed: 0, total: filesToUpload.length, currentName: null, phase: 'compress' });

    const result = await uploadFiles(
      filesToUpload,
      async (blob, name) => {
        const fd = new FormData();
        fd.append('files', blob, name);
        const { data } = await adminApi.post(`/proyectos/${id}/gallery`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        return data;
      },
      (p) => {
        setGalleryProgress(p);
        if (p.lastData) {
          const nextPatch = {};
          if (Array.isArray(p.lastData.gallery)) nextPatch.gallery = p.lastData.gallery;
          if (p.lastData.mainImage !== undefined) nextPatch.mainImage = p.lastData.mainImage;
          if (Object.keys(nextPatch).length) patch(nextPatch);
        }
      },
    );

    setUploadResult({
      inserted: result.inserted,
      errors: result.errors,
      failedFiles: result.failedFiles,
    });
    setUploadingGallery(false);
    setGalleryProgress(null);
  };

  const uploadGallery = async () => {
    const files = galleryRef.current?.files;
    if (!files || files.length === 0) { alert('Selecciona una o más imágenes'); return; }
    setUploadResult(null);
    const arr = Array.from(files);
    if (galleryRef.current) galleryRef.current.value = '';
    await runUploadGallery(arr);
  };

  const retryGalleryFailed = async () => {
    if (!uploadResult?.failedFiles?.length) return;
    const retryList = uploadResult.failedFiles;
    setUploadResult(null);
    await runUploadGallery(retryList);
  };

  const moveImg = async (idx, dir) => {
    const to = idx + dir;
    const arr = [...(item.gallery || [])];
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    patch({ gallery: arr });
    await adminApi.post(`/proyectos/${id}/gallery/reorder`, arr);
  };

  const deleteImg = async (url) => {
    if (!window.confirm('¿Eliminar esta imagen de la galería?')) return;
    try {
      const { data } = await adminApi.delete(`/proyectos/${id}/gallery`, { params: { url } });
      // Trust server response — no refetch (prevents CDN-cache/race issues where
      // deleted items "reappear" after refetching a stale GET).
      patch({ gallery: data.gallery || [], mainImage: data.mainImage ?? item.mainImage });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const clearAllGallery = async () => {
    if (!window.confirm(`¿Eliminar TODAS las ${(item.gallery || []).length} imágenes de la galería?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      const { data } = await adminApi.delete(`/proyectos/${id}/gallery/all`);
      patch({ gallery: data.gallery || [], mainImage: data.mainImage ?? '' });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error');
    }
  };

  const setAsMain = async (url) => {
    setUploadingField('mainImage');
    try {
      await adminApi.patch(`/proyectos/${id}`, { mainImage: url });
      patch({ mainImage: url });
    } finally { setUploadingField(null); }
  };

  const onLogout = () => { logoutAdmin(); window.location.href = '/admin/login'; };

  if (!item) return <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center"><Loader2 className="animate-spin text-[#58677A]"/></div>;

  return (
    <div className="min-h-screen bg-[#f4f5f7]" data-testid="admin-proyecto-edit-page">
      <header className="bg-white border-b border-[#58677A]/10 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/proyectos" className="text-[#58677A] hover:text-[#ED6B24] transition"><ArrowLeft size={18}/></Link>
            <div className="w-9 h-9 rounded-lg bg-[#ED6B24]/10 flex items-center justify-center"><ShieldCheck className="text-[#ED6B24]" size={18} /></div>
            <div>
              <p className="text-[15px] font-bold text-[#58677A] leading-tight" style={{fontFamily:'Gotham,sans-serif'}}>Editar proyecto</p>
              <p className="text-[11px] text-[#58677A]/60 truncate max-w-[40vw]">{item.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={`/proyectos/${item.slug}`} target="_blank" rel="noopener" className="text-xs text-[#58677A] hover:text-[#ED6B24] px-3 py-1.5 rounded-md hover:bg-[#ED6B24]/10">Ver ↗</a>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-sm font-semibold rounded-full px-4 py-2 disabled:opacity-60" data-testid="save-proyecto-btn">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Guardar
            </button>
            <button onClick={onLogout} className="text-xs text-[#58677A] hover:text-red-600 px-2 py-1.5 rounded-md hover:bg-red-50 inline-flex items-center gap-1" data-testid="admin-logout"><LogOut size={13}/> Salir</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Basic info */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6 space-y-4">
          <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Información básica</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Título del proyecto">
              <input value={item.title || ''} onChange={(e) => patch({title: e.target.value})} className={inputCls} data-testid="field-title"/>
            </Field>
            <Field label="Cliente">
              <input value={item.client || ''} onChange={(e) => patch({client: e.target.value})} className={inputCls} data-testid="field-client"/>
            </Field>
            <Field label="Subtítulo (opcional, ej. ubicaciones)">
              <input value={item.subtitle || ''} onChange={(e) => patch({subtitle: e.target.value})} className={inputCls} data-testid="field-subtitle"/>
            </Field>
            <Field label="Categoría">
              <select value={item.category || ''} onChange={(e) => patch({category: e.target.value})} className={inputCls} data-testid="field-category">
                {servicios.map((s) => <option key={s.slug} value={s.slug}>{s.name} ({s.slug})</option>)}
              </select>
            </Field>
            <Field label="Slug (URL: /proyectos/…)">
              <input value={item.slug || ''} onChange={(e) => patch({slug: e.target.value})} className={`${inputCls} font-mono text-sm`} data-testid="field-slug"/>
              <p className="text-[10px] text-orange-600 mt-1">⚠️ Cambiar el slug modifica la URL pública del proyecto.</p>
            </Field>
          </div>
          <Field label="Descripción (pulsa Enter para separar párrafos)">
            <textarea rows={5} value={item.description || ''} onChange={(e) => patch({description: e.target.value})} className={`${inputCls} leading-relaxed`} placeholder="Cada bloque separado por un Enter aparecerá como un párrafo distinto." data-testid="field-description"/>
          </Field>
        </section>

        {/* Cover + logos */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6 space-y-4">
          <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Imagen principal y logos</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <ImagePicker
              label="Imagen principal (portada del detalle)"
              value={item.mainImage}
              inputRef={mainRef}
              onUpload={() => uploadSingle('mainImage', mainRef)}
              busy={uploadingField === 'mainImage'}
              testId="mainImage"
              hint="Se usa en la portada del detalle y en cards. Máx 1600 px, JPG 82%."
              aspect="16/10"
            />
            <ImagePicker
              label="Logo del cliente"
              value={item.clientLogo}
              inputRef={clientLogoRef}
              onUpload={() => uploadSingle('clientLogo', clientLogoRef)}
              busy={uploadingField === 'clientLogo'}
              testId="clientLogo"
              hint="PNG con fondo transparente recomendado."
              aspect="16/10"
              logoStyle
            />
            <ImagePicker
              label="Logo destacado (Home masonry)"
              value={item.featuredLogo}
              inputRef={featuredLogoRef}
              onUpload={() => uploadSingle('featuredLogo', featuredLogoRef)}
              busy={uploadingField === 'featuredLogo'}
              testId="featuredLogo"
              hint="Solo si el proyecto es destacado."
              aspect="16/10"
              logoStyle
            />
          </div>
        </section>

        {/* Featured layout controls */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Destacado en Home</h2>
            <button
              onClick={() => patch({featuredOnHome: !item.featuredOnHome})}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${item.featuredOnHome ? 'bg-[#ED6B24] text-white' : 'bg-[#f4f5f7] text-[#58677A] hover:bg-[#ED6B24]/10'}`}
              data-testid="toggle-featured"
            >
              {item.featuredOnHome ? <><Star size={14} fill="currentColor"/> Destacado activo</> : <><StarOff size={14}/> No destacado</>}
            </button>
          </div>
          {item.featuredOnHome && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Tamaño del logo destacado">
                <select value={item.featuredLogoSize || 'default'} onChange={(e) => patch({featuredLogoSize: e.target.value})} className={inputCls} data-testid="field-featuredLogoSize">
                  {LOGO_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Aspect ratio de la card destacada">
                <select value={item.featuredAspect || ''} onChange={(e) => patch({featuredAspect: e.target.value})} className={inputCls} data-testid="field-featuredAspect">
                  {ASPECT_RATIOS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </Field>
            </div>
          )}
        </section>

        {/* Sections manager (multi-gallery) */}
        <SectionsManager
          projectId={id}
          sections={item.sections || []}
          onReload={load}
          onLocalUpdate={(newSections) => patch({ sections: newSections })}
          onLocalPatchSection={(sid, patchObj) => {
            const arr = (item.sections || []).map((s) => s.id === sid ? { ...s, ...patchObj } : s);
            patch({ sections: arr });
          }}
        />


        {/* Gallery manager */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10">
          <div className="px-5 sm:px-6 py-4 border-b border-[#58677A]/10 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Galería ({(item.gallery || []).length})</h2>
              <p className="text-xs text-[#58677A]/60 mt-0.5">Sube múltiples imágenes (se comprimen en tu navegador y se envían una por una para máxima estabilidad). Reordena con flechas. Marca cualquiera como imagen principal.</p>
            </div>
            <div className="flex items-center gap-2">
              <input ref={galleryRef} type="file" accept="image/*" multiple disabled={uploadingGallery} className="hidden" id="gallery-input" data-testid="gallery-input"/>
              <label htmlFor="gallery-input" className={`inline-flex items-center gap-1.5 bg-[#f4f5f7] hover:bg-[#ED6B24]/10 text-[#58677A] text-xs font-semibold rounded-md px-3 py-2 border border-[#58677A]/20 ${uploadingGallery ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                <ImagePlus size={13}/> Seleccionar imágenes
              </label>
              <button onClick={uploadGallery} disabled={uploadingGallery} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-xs font-semibold rounded-md px-3 py-2 disabled:opacity-60" data-testid="upload-gallery-btn">
                {uploadingGallery ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>} Subir
              </button>
              {(item.gallery || []).length > 0 && (
                <button
                  onClick={clearAllGallery}
                  className="inline-flex items-center gap-1.5 text-red-600 border border-red-300 hover:bg-red-500 hover:text-white text-xs font-semibold rounded-md px-3 py-2"
                  data-testid="clear-gallery-btn"
                >
                  <Trash2 size={13}/> Eliminar todas
                </button>
              )}
            </div>
          </div>
          {galleryProgress && galleryProgress.total > 0 && (
            <div className="mx-5 sm:mx-6 mt-4" data-testid="gallery-upload-progress">
              <div className="flex items-center justify-between text-xs text-[#58677A] mb-1.5">
                <span className="font-semibold">
                  {galleryProgress.phase === 'compress' ? 'Optimizando' : 'Subiendo'} {galleryProgress.completed} de {galleryProgress.total}
                  {galleryProgress.currentName && (
                    <span className="text-[#58677A]/60 font-normal font-mono ml-1">· {galleryProgress.currentName.length > 32 ? galleryProgress.currentName.slice(0, 32) + '…' : galleryProgress.currentName}</span>
                  )}
                </span>
                <span className="tabular-nums">{Math.round((galleryProgress.completed / galleryProgress.total) * 100)}%</span>
              </div>
              <div className="w-full h-2 bg-[#f4f5f7] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#ED6B24] transition-[width] duration-300"
                  style={{ width: `${(galleryProgress.completed / galleryProgress.total) * 100}%` }}
                />
              </div>
              {galleryProgress.errors > 0 && (
                <p className="text-[11px] text-red-600 mt-1">{galleryProgress.errors} error(es) hasta ahora — el resto continúa.</p>
              )}
            </div>
          )}
          {uploadResult && (
            <div className="mx-5 sm:mx-6 mt-4 text-xs bg-green-50 border border-green-200 text-green-700 rounded-lg px-3 py-2" data-testid="upload-gallery-result">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  ✓ {uploadResult.inserted} imagen(es) añadidas.
                  {uploadResult.errors.length > 0 && (
                    <div className="text-red-600 mt-1">
                      {uploadResult.errors.length} error(es):
                      <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                        {uploadResult.errors.map((e, i) => (
                          <li key={i} className="text-[11px]"><span className="font-mono">{e.name}</span>: {e.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {uploadResult.failedFiles?.length > 0 && !uploadingGallery && (
                  <button
                    onClick={retryGalleryFailed}
                    className="shrink-0 inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-xs font-semibold rounded-md px-3 py-1.5"
                    data-testid="retry-gallery-failed-btn"
                  >
                    <RefreshCw size={12}/> Reintentar {uploadResult.failedFiles.length}
                  </button>
                )}
              </div>
            </div>
          )}

          {(item.gallery || []).length === 0 && (
            <div className="p-10 text-center text-[#58677A]/60 text-sm">Sin imágenes. Sube las primeras para comenzar.</div>
          )}

          {(item.gallery || []).length > 0 && (
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 p-4 sm:p-5">
              {(item.gallery || []).map((url, i) => {
                const isMain = item.mainImage === url;
                return (
                  <li key={url} className="relative group rounded-xl overflow-hidden border border-[#58677A]/10 bg-[#f4f5f7]" data-testid={`gallery-item-${i}`}>
                    <div className="aspect-square overflow-hidden">
                      <img src={toUrl(url)} alt="" loading="lazy" className="w-full h-full object-cover"/>
                    </div>
                    <div className="absolute top-1.5 left-1.5 text-[10px] font-mono bg-black/70 text-white rounded px-1.5 py-0.5">#{i + 1}</div>
                    {isMain && <div className="absolute top-1.5 right-1.5 flex items-center gap-1 text-[10px] font-bold bg-[#ED6B24] text-white rounded-full px-2 py-1"><Star size={10} fill="currentColor"/> Principal</div>}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
                      <button onClick={() => moveImg(i, -1)} disabled={i===0} className="w-8 h-8 rounded-md bg-white/95 text-[#58677A] hover:bg-[#ED6B24] hover:text-white disabled:opacity-40 flex items-center justify-center" title="Subir" data-testid={`gallery-up-${i}`}><ArrowUp size={13}/></button>
                      <button onClick={() => moveImg(i, 1)} disabled={i===(item.gallery || []).length-1} className="w-8 h-8 rounded-md bg-white/95 text-[#58677A] hover:bg-[#ED6B24] hover:text-white disabled:opacity-40 flex items-center justify-center" title="Bajar" data-testid={`gallery-down-${i}`}><ArrowDown size={13}/></button>
                      {!isMain && (
                        <button onClick={() => setAsMain(url)} className="w-8 h-8 rounded-md bg-white/95 text-[#ED6B24] hover:bg-[#ED6B24] hover:text-white flex items-center justify-center" title="Marcar como principal" data-testid={`gallery-setmain-${i}`}><Star size={13}/></button>
                      )}
                      <button onClick={() => deleteImg(url)} className="w-8 h-8 rounded-md bg-white/95 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center" title="Eliminar" data-testid={`gallery-delete-${i}`}><Trash2 size={13}/></button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-sm font-semibold rounded-full px-5 py-2.5 disabled:opacity-60" data-testid="save-proyecto-btn-bottom">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </main>
    </div>
  );
}

const inputCls = 'w-full border border-[#58677A]/20 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ED6B24] focus:ring-2 focus:ring-[#ED6B24]/10 bg-white text-[#58677A]';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-[#58677A]/70 block mb-1">{label}</span>
    {children}
  </label>
);

const ImagePicker = ({ label, value, inputRef, onUpload, busy, testId, hint, aspect = '16/10', logoStyle = false }) => (
  <div>
    <p className="text-xs text-[#58677A]/70 mb-2">{label}</p>
    <div className={`w-full bg-[#f4f5f7] rounded-xl overflow-hidden border border-[#58677A]/10 mb-2 relative`} style={{ aspectRatio: aspect }}>
      {value ? (
        <img src={toUrl(value)} alt="" className={`w-full h-full ${logoStyle ? 'object-contain p-3' : 'object-cover'}`} data-testid={`preview-${testId}`}/>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-[#58677A]/40 gap-1"><ImageIcon size={24}/><span className="text-[10px]">Sin imagen</span></div>
      )}
    </div>
    <div className="flex items-center gap-1.5">
      <input ref={inputRef} type="file" accept="image/*" className="text-[11px] text-[#58677A] flex-1 min-w-0 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#ED6B24]/10 file:text-[#ED6B24] file:font-semibold file:cursor-pointer" data-testid={`file-${testId}`}/>
      <button onClick={onUpload} disabled={busy} className="inline-flex items-center gap-1 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-[11px] font-semibold rounded px-2.5 py-1.5 disabled:opacity-60" data-testid={`upload-${testId}`}>
        {busy ? <Loader2 size={11} className="animate-spin"/> : <Upload size={11}/>}
      </button>
    </div>
    {hint && <p className="text-[10px] text-[#58677A]/50 mt-1">{hint}</p>}
  </div>
);

// ============================================================================
// SectionsManager — multi-gallery sections (title + images per section)
// ============================================================================
function SectionsManager({ projectId, sections, onReload, onLocalUpdate, onLocalPatchSection }) {
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const add = async () => {
    setCreating(true);
    try {
      await adminApi.post(`/proyectos/${projectId}/sections`, { title: newTitle.trim() });
      setNewTitle('');
      onReload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al crear sección');
    } finally { setCreating(false); }
  };

  const rename = async (sid, title) => {
    try { await adminApi.patch(`/proyectos/${projectId}/sections/${sid}`, { title }); } catch {}
  };

  const del = async (sid, title) => {
    if (!window.confirm(`¿Eliminar la sección "${title || '(sin título)'}"?\n\nSe eliminarán todas las imágenes subidas de esta sección.`)) return;
    await adminApi.delete(`/proyectos/${projectId}/sections/${sid}`);
    onReload();
  };

  const moveSection = async (idx, dir) => {
    const arr = [...sections];
    const to = idx + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    onLocalUpdate(arr);
    await adminApi.post(`/proyectos/${projectId}/sections/reorder`, arr.map((s) => s.id));
  };

  return (
    <section className="bg-white rounded-2xl border border-[#58677A]/10">
      <div className="px-5 sm:px-6 py-4 border-b border-[#58677A]/10 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Galerías con títulos ({sections.length})</h2>
          <p className="text-xs text-[#58677A]/60 mt-0.5">Divide el proyecto en varias sub-galerías (ej. "Evento ColorMe 2026"). Cada una con su título y sus imágenes.</p>
        </div>
      </div>

      <div className="px-5 sm:px-6 pt-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-[#58677A]/70 block mb-1">Título de la nueva sección (opcional)</label>
          <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Ej. Evento ColorMe 2026 - Quito, Guayaquil y Cuenca" className="w-full border border-[#58677A]/20 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ED6B24] text-[#58677A] text-sm" data-testid="new-section-title"/>
        </div>
        <button onClick={add} disabled={creating} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-xs font-semibold rounded-md px-4 py-2 disabled:opacity-60" data-testid="add-section-btn">
          {creating ? <Loader2 size={13} className="animate-spin"/> : <ImagePlus size={13}/>} Añadir sección
        </button>
      </div>

      <div className="px-5 sm:px-6 py-4 space-y-4">
        {sections.length === 0 && (
          <div className="p-6 text-center text-[#58677A]/60 text-sm bg-[#f4f5f7] rounded-lg">Sin secciones. Añade la primera con un título arriba.</div>
        )}
        {sections.map((sec, idx) => (
          <SectionCard
            key={sec.id}
            section={sec}
            index={idx}
            total={sections.length}
            projectId={projectId}
            onRename={(title) => rename(sec.id, title)}
            onDelete={() => del(sec.id, sec.title)}
            onMove={(dir) => moveSection(idx, dir)}
            onReload={onReload}
            onLocalPatchSection={onLocalPatchSection}
          />
        ))}
      </div>
    </section>
  );
}

function SectionCard({ section, index, total, projectId, onRename, onDelete, onMove, onReload, onLocalPatchSection }) {
  const [title, setTitle] = useState(section.title || '');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef();

  useEffect(() => { setTitle(section.title || ''); }, [section.title]);

  const upload = async () => {
    const files = fileRef.current?.files;
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    if (fileRef.current) fileRef.current.value = '';
    setUploading(true);
    setProgress({ completed: 0, total: arr.length, currentName: null, phase: 'compress' });

    const result = await uploadFiles(
      arr,
      async (blob, name) => {
        const fd = new FormData();
        fd.append('files', blob, name);
        const { data } = await adminApi.post(
          `/proyectos/${projectId}/sections/${section.id}/images`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 },
        );
        return data;
      },
      (p) => {
        setProgress(p);
        if (p.lastData && Array.isArray(p.lastData.images)) {
          onLocalPatchSection && onLocalPatchSection(section.id, { images: p.lastData.images });
        }
      },
    );

    setUploading(false);
    setProgress(null);

    if (result.errors.length > 0) {
      alert(`${result.inserted} imagen(es) añadidas. ${result.errors.length} error(es):\n\n` +
        result.errors.slice(0, 15).map((e) => `• ${e.name}: ${e.error}`).join('\n') +
        (result.errors.length > 15 ? `\n… y ${result.errors.length - 15} más.` : ''));
    }
    // Safety: refetch server state if we didn't receive any successful batch response
    if (!result.lastData) onReload();
  };

  const moveImg = async (i, dir) => {
    const arr = [...(section.images || [])];
    const to = i + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[i], arr[to]] = [arr[to], arr[i]];
    await adminApi.post(`/proyectos/${projectId}/sections/${section.id}/reorder-images`, arr);
    onReload();
  };

  const delImg = async (url) => {
    if (!window.confirm('¿Eliminar esta imagen?')) return;
    try {
      const { data } = await adminApi.delete(`/proyectos/${projectId}/sections/${section.id}/images`, { params: { url } });
      // Trust server response to avoid stale-refetch bug
      onLocalPatchSection && onLocalPatchSection(section.id, { images: data.images || [] });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error');
    }
  };

  const clearAllInSection = async () => {
    if (!window.confirm(`¿Eliminar TODAS las ${(section.images || []).length} imágenes de esta sección?`)) return;
    try {
      const { data } = await adminApi.delete(`/proyectos/${projectId}/sections/${section.id}/images/all`);
      onLocalPatchSection && onLocalPatchSection(section.id, { images: data.images || [] });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="rounded-xl border border-[#58677A]/15 bg-[#f4f5f7]/40" data-testid={`section-card-${section.id}`}>
      <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-[#58677A]/10">
        <span className="text-[10px] font-mono text-[#58677A]/50 shrink-0">#{index + 1}</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== section.title && onRename(title)}
          placeholder="Título de la sección (opcional)"
          className="flex-1 min-w-[180px] bg-white border border-[#58677A]/20 rounded-md px-3 py-1.5 text-sm text-[#58677A] focus:outline-none focus:border-[#ED6B24]"
          data-testid={`section-title-${section.id}`}
        />
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={index===0} className="w-7 h-7 rounded flex items-center justify-center text-[#58677A] hover:bg-[#ED6B24]/10 disabled:opacity-30" title="Subir sección"><ArrowUp size={13}/></button>
          <button onClick={() => onMove(1)} disabled={index===total-1} className="w-7 h-7 rounded flex items-center justify-center text-[#58677A] hover:bg-[#ED6B24]/10 disabled:opacity-30" title="Bajar sección"><ArrowDown size={13}/></button>
          <button onClick={onDelete} className="w-7 h-7 rounded flex items-center justify-center text-red-500 hover:bg-red-50" title="Eliminar sección" data-testid={`delete-section-${section.id}`}><Trash2 size={13}/></button>
        </div>
      </div>

      <div className="px-4 py-3 flex items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*" multiple disabled={uploading} className="hidden" id={`sec-file-${section.id}`}/>
        <label htmlFor={`sec-file-${section.id}`} className={`inline-flex items-center gap-1.5 bg-white hover:bg-[#ED6B24]/10 text-[#58677A] text-xs font-semibold rounded-md px-3 py-1.5 border border-[#58677A]/20 ${uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}><ImagePlus size={12}/> Seleccionar</label>
        <button onClick={upload} disabled={uploading} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-xs font-semibold rounded-md px-3 py-1.5 disabled:opacity-60" data-testid={`upload-section-${section.id}`}>
          {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Subir
        </button>
        {(section.images || []).length > 0 && (
          <button onClick={clearAllInSection} disabled={uploading} className="inline-flex items-center gap-1.5 text-red-600 border border-red-300 hover:bg-red-500 hover:text-white text-xs font-semibold rounded-md px-3 py-1.5 disabled:opacity-60" data-testid={`clear-section-${section.id}`}>
            <Trash2 size={12}/> Eliminar todas
          </button>
        )}
        <span className="text-xs text-[#58677A]/60 ml-auto">{(section.images || []).length} img</span>
      </div>

      {progress && progress.total > 0 && (
        <div className="px-4 pb-2">
          <div className="flex items-center justify-between text-[11px] text-[#58677A] mb-1">
            <span className="font-semibold">
              {progress.phase === 'compress' ? 'Optimizando' : 'Subiendo'} {progress.completed}/{progress.total}
              {progress.currentName && <span className="text-[#58677A]/60 font-normal ml-1">· {progress.currentName.length > 24 ? progress.currentName.slice(0, 24) + '…' : progress.currentName}</span>}
            </span>
            <span className="tabular-nums">{Math.round((progress.completed / progress.total) * 100)}%</span>
          </div>
          <div className="w-full h-1.5 bg-white rounded-full overflow-hidden">
            <div className="h-full bg-[#ED6B24] transition-[width] duration-300" style={{ width: `${(progress.completed / progress.total) * 100}%` }} />
          </div>
          {progress.errors > 0 && <p className="text-[10px] text-red-600 mt-0.5">{progress.errors} error(es) · continuando…</p>}
        </div>
      )}

      {(section.images || []).length > 0 && (
        <ul className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 px-4 pb-4">
          {(section.images || []).map((url, i) => (
            <li key={url} className="relative group rounded overflow-hidden border border-[#58677A]/10 bg-white aspect-square">
              <img src={toUrl(url)} alt="" loading="lazy" className="w-full h-full object-cover"/>
              <div className="absolute top-1 left-1 text-[9px] font-mono bg-black/70 text-white rounded px-1">#{i+1}</div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                <button onClick={() => moveImg(i, -1)} disabled={i===0} className="w-6 h-6 rounded bg-white/95 text-[#58677A] hover:bg-[#ED6B24] hover:text-white disabled:opacity-40 flex items-center justify-center"><ArrowUp size={11}/></button>
                <button onClick={() => moveImg(i, 1)} disabled={i===(section.images||[]).length-1} className="w-6 h-6 rounded bg-white/95 text-[#58677A] hover:bg-[#ED6B24] hover:text-white disabled:opacity-40 flex items-center justify-center"><ArrowDown size={11}/></button>
                <button onClick={() => delImg(url)} className="w-6 h-6 rounded bg-white/95 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center"><Trash2 size={11}/></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

